import { VisitType } from "@/api/enums";
import type {
  SpecialistPractice,
  SpecialistSlot,
  SpecialistsDetails,
} from "@/api/contracts/specialists.contract";
import { getSlotDateDisplay, type SlotLike } from "./appointments";

export function formatDoctorName(s: {
  firstName?: string | null;
  lastName?: string | null;
  suffix?: string | null;
}): string {
  const base = [s.firstName, s.lastName].filter(Boolean).join(" ");
  return s.suffix ? `${base}, ${s.suffix}` : base;
}

export function formatSearchLocation(practice: SpecialistPractice): string {
  return (
    [
      practice.practiceName,
      practice.practiceAddress,
      practice.practiceCity,
      practice.practiceState,
    ]
      .filter(Boolean)
      .join(", ") || "Telehealth Only"
  );
}

export function formatSearchLocationShort(
  practice: SpecialistPractice,
): string {
  return (
    [practice.practiceCity, practice.practiceState]
      .filter(Boolean)
      .join(", ") ||
    "Telehealth Only"
  );
}

export function getSpecialistPrice(specialist: SpecialistsDetails): number {
  const primaryPractice = specialist.practices[0];
  return (
    primaryPractice?.specialistInPersonPricing ??
    specialist.telemedicine.specialistTelemedicinePricing ??
    primaryPractice?.defaultFee ??
    0
  );
}

export type VisitTypeDisplay = "in-person" | "telehealth" | "both";

export function getSpecialistVisitType(
  specialist: SpecialistsDetails,
): VisitTypeDisplay {
  if (specialist.specialistVisitType === VisitType.Telemedicine)
    return "telehealth";
  if (specialist.specialistVisitType === VisitType.Both) return "both";
  const hasIn = specialist.practices.some(
    (practice) => practice.AvailableSlots.length > 0,
  );
  const hasTel = specialist.telemedicine.AvailableSlots.length > 0;
  return hasIn && hasTel ? "both" : hasTel ? "telehealth" : "in-person";
}

type AvailabilitySlot = SpecialistSlot & Pick<SlotLike, "locationId">;

/** A single "date · location" block of bookable time slots within a card. */
export type SearchAvailabilityGroup = {
  key: string;
  date: string;
  locationLabel: string;
  isTelehealth: boolean;
  /** Stable id of the matching map marker (`specialistId__locationId`); null for telehealth. */
  mapKey: string | null;
  slots: AvailabilitySlot[];
};

/** Unique id shared by a location's map marker and its availability blocks. */
export function locationMapKey(
  specialistId: string,
  locationId: string,
): string {
  return `${specialistId}__${locationId}`;
}

/**
 * Build chronologically-sorted blocks from the slots nested under each practice
 * and the specialist's telemedicine availability. The parent supplies the
 * location context intentionally omitted from the slim slot response.
 */
export function buildSearchAvailability(
  specialist: SpecialistsDetails,
): SearchAvailabilityGroup[] {
  const seen = new Set<string>();
  const groups = new Map<string, SearchAvailabilityGroup>();

  const addSlots = (
    slots: SpecialistSlot[],
    locationId: string | null,
    locationLabel: string,
    isTelehealth: boolean,
  ) => {
    for (const slot of slots) {
      if (seen.has(slot.id)) continue;
      seen.add(slot.id);

      const availabilitySlot: AvailabilitySlot = { ...slot, locationId };
      const date = getSlotDateDisplay(availabilitySlot);
      const key = `${date}__${locationId ?? "telehealth"}`;

      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          date,
          isTelehealth,
          mapKey: locationId
            ? locationMapKey(specialist.specialistId, locationId)
            : null,
          locationLabel,
          slots: [],
        };
        groups.set(key, group);
      }
      group.slots.push(availabilitySlot);
    }
  };

  for (const practice of specialist.practices) {
    addSlots(
      practice.AvailableSlots,
      practice.practiceLocationId,
      formatSearchLocation(practice),
      false,
    );
  }
  addSlots(
    specialist.telemedicine.AvailableSlots,
    null,
    "Telehealth",
    true,
  );

  const result = Array.from(groups.values());
  for (const group of result) {
    group.slots.sort(
      (a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
    );
  }
  result.sort((a, b) => {
    const ta = new Date(a.slots[0].dateTime).getTime();
    const tb = new Date(b.slots[0].dateTime).getTime();
    if (ta !== tb) return ta - tb;
    return a.locationLabel.localeCompare(b.locationLabel);
  });
  return result;
}
